<template>
    <div>
        <!--Create or Update Bookmark-->
        <v-dialog
            v-if="modal"
            v-model="modal"
            persistent
            :fullscreen="isMobilePort"
            max-width="1200px"
            width="90%"
            scrollable>
            <v-card :style="{
                'max-height': isMobilePort ? '100%' : '95vh',
                'overflow-y': 'auto'
            }">
                <v-card-title class="primary white--text" :class="{
                    'headline': !isMobilePort,
                    'caption mobile-title': isMobilePort
                }">
                    <span v-if="isUpdate">Edit Note / Bookmark</span>
                    <span v-else>Add Note / Bookmark</span>
                    <v-spacer></v-spacer>
                    <v-icon class="primary white--text" @click="closeModal">close</v-icon>
                </v-card-title>
                <v-card-text :class="isMobilePort ? 'px-3' : 'px-4 pt-0 pb-4'">
                    <v-row dense v-if="isContentLoading" :class="isMobilePort ? '' : 'pt-4'">
                        <v-col class="text-center">
                            <v-progress-circular
                                indeterminate
                                color="primary">
                            </v-progress-circular>
                            <div class="mt-2 text-h6 font-weight-medium mb-2">
                                Loading section content...
                            </div>
                        </v-col>
                    </v-row>
                    <v-row dense v-else>
                        <v-col
                            class="mb-2"
                            :style="{
                            'max-height': isMobilePort ? '100%' : '40vh',
                            'overflow-y': 'auto',
                            'padding-top': '17px'
                        }">
                            <default-content-section-controller
                                v-show="contentSections.length"
                                highlightType="insert-into"
                                :contentSections="contentSections"
                                :registerDialogs="false"
                                :renderToolbar="false"
                                :showTitleAsLink="false"
                                :displayBreadCrumbs="false"
                                :hideContentLinks="true"
                                :displaySectionSeparated="false"/>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col>
                            <div
                                class="highlight-text"
                                :style="{ '--blockquote-border-color': selectedTagObj?.color ? '#' + selectedTagObj?.color : '#eee' }"
                            >
                                <vue-editor class="custom-editor" v-model="editorData" :editor-toolbar="customToolbar"/>
                            </div>
                        </v-col>
                    </v-row>
                    <div v-if="!loadingTags" class="d-flex align-center mt-3" style="gap: 8px;">
                        <v-autocomplete
                            style="max-width: 480px;"
                            v-model="selectedTag"
                            :items="tags"
                            item-text="tag"
                            item-value="id"
                            chips
                            small-chips
                            outlined
                            dense
                            label="Project Tag"
                            :rules="[v => !!v || 'Project tag is required']"
                            hide-details="auto"
                            ref="projectTag"
                            @change="updateSelectedTag"
                        >
                            <template v-slot:selection="data">
                                <v-chip
                                    small
                                    :key="selectedTagObj?.id"
                                    :style="{ backgroundColor: '#' + selectedTagObj?.color, color: '#000' }"
                                    close
                                    @click:close="removeSelectedTag"
                                >
                                    {{ data.item.tag }}
                                </v-chip>
                            </template>
                        </v-autocomplete>

                        <v-btn
                            :class="isMobilePort ? 'pa-0' : ''"
                            v-if="selectedTag !== 1"
                            outlined
                            @click="createNewTagModal(true, selectedTag)">
                            Edit Tag
                        </v-btn>
                        <v-btn
                            :class="isMobilePort ? 'pa-0' : ''"
                            outlined
                            @click="createNewTagModal(false, selectedTag)">
                            Add Tag
                        </v-btn>
                    </div>
                    <template v-else>
                        <v-row>
                            <v-col>
                                <v-progress-linear :indeterminate="true"></v-progress-linear>
                            </v-col>
                        </v-row>
                    </template>
                    <div v-if="activeEnterpriseProfile" class="d-flex align-center mt-3">
                        <v-select
                                v-model="selectedVisibility"
                                :items="visibilityItems"
                                label="Visibility"
                                outlined
                                flat
                                hide-details
                                dense
                                style="max-width: 480px;">
                                <template v-slot:selection="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>

                                <template v-slot:item="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>
                            </v-select>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeModal">Cancel</v-btn>
                    <v-btn color="primary" :disabled="isContentLoading" :loading="isSaving" @click="saveNote">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Loading from '../../content/partials/loading.vue';
import TagsMixins from '../mixins/Tags';
import urlHelper from '../mixins/UrlHelper';
import EditorConfigs from '../../inc/editorConfigBar';
import Mobile from '../../inc/mixins/mobile';
import { EventNames } from '../../../classes/EventNames';
import { NotesPageApi } from '../../../api/NotesPageApi';
import { ContentSectionApi } from '../../../api/ContentSectionApi';
import {showNotification} from "../../../global";

export default {
    components: {
        DefaultContentSectionController: () => import('../DefaultContentSectionController.vue'),
        Loading
    },
    data() {
        return {
            modal: false,
            note: {},
            editorData: '',
            isUpdate: false,
            invalidNoteUrl: false,
            sectionId: '',
            isSaving: false,
            isContentLoading: false,
            contentSections: [],
            // Visibility
            selectedVisibility: 'private',
            visibilityItems: [
                {value: 'private', text: 'Private (visible to just you)', icon: 'mdi-eye-off-outline'},
                {value: 'public', text: 'Public (visible to everyone in your Enterprise)', icon: 'mdi-eye-outline'}
            ],
            isAnnotation: false
        }
    },
    mixins: [TagsMixins, urlHelper, Mobile, EditorConfigs],
    computed: {
        currentUserId() {
            return parseInt(this.$store?.getters?.getUserId);
        }
    },
    methods: {
        async saveNote() {
            if (!this.validateLinksInNote(this.editorData)) {
                this.invalidNoteUrl = true;
                return false;
            }

            const isValid = this.$refs.projectTag.validate();
            if (!isValid) return;

            this.isSaving = true;

            try {
                const noteObj = this.getNoteObject();

                const response = this.isAnnotation
                    ? await NotesPageApi.saveAnnotation(noteObj)
                    : await NotesPageApi.saveBookmark(noteObj);

                const [noteData] = response.data;

                if (!noteData) throw new Error("Invalid note response");

                this.selectedVisibility = noteData.is_public ? 'public' : 'private';

                //update notes list
                this.notifyUpdates(noteData);

                this.closeModal();

                // Hide private bookmark immediately if applicable
                if (this.hideableNote(noteData) && this.selectedVisibility === 'private') {
                    const sectionId = this.isAnnotation ? noteData.section_number : noteData.section_id;
                    this.hidePrivateNote(noteData.id, sectionId);
                }
                showNotification('Note saved successfully', 'success');
            } catch (error) {
                console.error("Failed to save note:", error);
                showNotification('Error saving note. Please refresh and try again.', 'error');
            } finally {
                this.isSaving = false;
            }
        },
        async getBookmark(id) {
            this.isUpdate = true;
            this.isSaving = true;
            try {
                const { data } = await NotesPageApi.getBookmark(id);
                this.setNoteData(data);
            } catch (e) {
                console.error("Failed to fetch bookmark", e);
            } finally {
                this.isSaving = false;
            }
        },
        async getAnnotation(id) {
            this.isUpdate = true;
            this.isSaving = true;
            try {
                const { data } = await NotesPageApi.getAnnotation(id);
                this.setNoteData(data, true);
            } catch (e) {
                console.error("Failed to fetch annotation", e);
            } finally {
                this.isSaving = false;
            }

        },
        setNoteData(data) {
            this.note = this.isAnnotation
                ? { id: data.id, annotation: data.annotation }
                : data;

            this.editorData = this.isAnnotation ? data.annotation.text : data.text;
            this.getUserTags(data.tag);
            this.tagId = _.toInteger(data.tag);
            this.selectedVisibility = data?.is_public ? 'public' : 'private';
        },
        closeModal(){
            this.note = {};
            this.editorData = '';
            this.tags = [];
            this.selectedTag = [];
            this.selectedTagObj = {};
            this.isUpdate = false;
            this.loadingTags = true;

            this.modal = false;
        },
        //TODO: Define a prop structure for better readability and handling
        onBookmarkModal(bookmark) {
            this.isAnnotation = false;
            this.openModal(bookmark, 'bookmark');
        },
        onAnnotationModal(note) {
            this.isAnnotation = true;
            this.openModal(note, 'note');
        },
        openModal(note, type) {
            this.getSectionContentBySectionId(note.sectionId);

            this.isUpdate = !!note?.id;
            this.note = note;
            this.selectedVisibility = this.activeEnterpriseProfile ? 'public' : 'private';

            if (note?.id) {
                type === 'bookmark' ? this.getBookmark(note.id) : this.getAnnotation(note.id);
            } else {
                this.getUserTags(1);
                if (note.quoteText) {
                    this.editorData = this.formatBlockQuote(note.quoteText);
                } else {
                    this.editorData = '';
                }
            }

            this.modal = true;
        },
        insertQuoteIntoNote(event) {
            if (event?.quoteText) {
                this.editorData += this.formatBlockQuote(event.quoteText);
            }
        },
        formatBlockQuote(text) {
            return `<blockquote>${text}</blockquote><p></p>`;
        },
        hidePrivateNote(noteId, sectionId) {
            const type = this.isAnnotation ? 'annotation' : 'bookmark';
            let options = {
                action: 'remove',
                type: type,
                id: noteId,
                sectionId: sectionId
            };
            EventBus.fire('update-notes', options);
        },
        hideableNote(noteObj) {
            return noteObj?.enterprise && this.isEnterpriseAdmin
                && this.currentUserId !== parseInt(noteObj?.user?.id);
        },
        getNoteObject() {
            //TODO: Make a common request for both
            const baseNote = {
                note: this.editorData,
                tag: this.selectedTag,
                visibility: this.selectedVisibility,
            };

            //annotation objects
            if (this.isAnnotation) {
                return {
                    ...this.note,
                    ...baseNote,
                };
            }
            //bookmark objects
            return {
                ...baseNote,
                data: this.note,
            };
        },
        notifyUpdates(noteData) {
            const type = this.isAnnotation ? 'annotation' : 'bookmark';
            const sectionKey = this.isAnnotation ? 'section_number' : 'section_id';

            const options = {
                action: this.isUpdate ? 'update' : 'create',
                sectionId: noteData[sectionKey],
                type,
                id: this.isUpdate ? noteData.id : undefined,
                notes: {
                [type === 'annotation' ? 'annotations' : 'bookmarks']: [noteData]
                }
            };

            if (this.isUpdate) {
                noteData.isUpdate = true;
                EventBus.fire(`update-note-preview-${noteData.id}`, noteData);
            }

            EventBus.fire('update-notes', options);
        },
        async getSectionContentBySectionId(id) {
            this.isContentLoading = true;
            try {
                const { data } = await ContentSectionApi.getSectionContentBySectionId(id);
                this.contentSections = data && data.section ? [data.section] : [];
            } catch (e) {
                console.error("Failed to fetch content", e);
            } finally {
                this.isContentLoading = false;
            }
        },
    },
    mounted() {
        EventBus.listen(EventNames.BOOKMARK_MODAL, this.onBookmarkModal);
        EventBus.listen(EventNames.NOTE_MODAL, this.onAnnotationModal);
        EventBus.listen(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        EventBus.listen(EventNames.INSERT_QUOTE_INTO_NOTE, this.insertQuoteIntoNote);
    },

    beforeDestroy() {
        EventBus.off(EventNames.BOOKMARK_MODAL, this.onBookmarkModal);
        EventBus.off(EventNames.NOTE_MODAL, this.onAnnotationModal);
        EventBus.off(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        EventBus.off(EventNames.INSERT_QUOTE_INTO_NOTE, this.insertQuoteIntoNote);
    }

}
</script>
<style lang="scss" scoped>
    .custom-editor .ql-editor {
        min-height: 120px !important;
    }
    .highlight-text ::v-deep blockquote {
        border-left: 4px solid var(--blockquote-border-color) !important;
    }
</style>